import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@baluu/ui/components/button";
import { Spinner } from "@baluu/ui/components/spinner";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson, messageOf, sendJson } from "../api";
import { BookingLoading, BookingProblem, Notice, RecordNotFound } from "../parts";
import { isOtherSchool } from "../school";
import { useAuthSnapshot } from "~/widgets/lib/auth-client";
import { formatCurrency } from "~/widgets/lib/utils";
import { BackToBooking, ClassSummary } from "./reschedule/FlowParts";
import { SessionPicker } from "./reschedule/SessionPicker";
import { authInit } from "./reschedule/request";
import { appendUnique, formatLongDate, formatNumericDate, toSessionRow, toggleSelection } from "./reschedule/sessions";
import { safeTimezone, viewerTimezone } from "../timezones";
import type { ApiBundle, ApiBundleTicket, ApiCourse, LocationLike, SessionLike, TutorLike } from "./reschedule/apiTypes";
import {
  DATES_PAGE_SIZE,
  canSaveDates,
  classesLeft,
  classesLeftTitle,
  isBundleDateDisabled,
  protectSelection,
  recordSessionIds,
  saveLabel,
  savedMessage,
  type ReschedulePolicy,
} from "./bundle-dates/bundleDates";

// /bookings/bundle/<booking-id>: choose (or change) the dates a bundle
// booking is used on. A port of v1's bundle page (BundleDatesClient +
// BundleDatesSelection): the bundle's classes left and expiry, the course's
// upcoming dates a page at a time, booked dates locked inside the school's
// reschedule notice, and saving the choice. The id is the bundle BOOKING's
// (GET /widget/pages/:school/bundle/:id reads a Booking).

type BundlePage = {
  bundle: ApiBundle;
  course: ApiCourse;
  locations?: LocationLike[];
  sessions?: SessionLike[];
  tutors?: TutorLike[];
};

type General = { school?: { settings?: { reschedulePolicy?: ReschedulePolicy } } };

export function BundleDatesView({ ctx, bookingId }: { ctx: ViewContext; bookingId: string }) {
  const { token } = useAuthSnapshot();
  const slug = ctx.businessSlug;

  const page = useQuery({
    queryKey: ["booking", "bundle-dates", ctx.api, slug, bookingId, token],
    enabled: !!slug,
    queryFn: () =>
      bookingJson<BundlePage>(
        `${ctx.api}/widget/pages/${encodeURIComponent(slug)}/bundle/${encodeURIComponent(bookingId)}`,
        authInit(token),
      ),
    retry: (count, error) => !(error instanceof BookingApiError && error.status < 500) && count < 2,
  });
  // The school's reschedule notice (v1 reads it off the app's school).
  const general = useQuery({
    queryKey: ["booking", "school-general", ctx.api, slug],
    enabled: !!slug,
    queryFn: () => bookingJson<General>(`${ctx.api}/widget/general/${encodeURIComponent(slug)}`),
    staleTime: 5 * 60_000,
  });

  const elsewhere = !!page.data && isOtherSchool(ctx.businessId, page.data.bundle?.school, page.data.course?.schoolId);
  const isMarketplace = !elsewhere && page.data?.bundle?.source === "obby";
  useEffect(() => {
    if (isMarketplace) ctx.toMarketplace(`/bookings/bundle/${bookingId}`);
  }, [isMarketplace, ctx, bookingId]);

  const back = <BackToBooking href={ctx.links.manage(bookingId)} />;

  if (!slug) return <BookingProblem title="This page isn’t set up yet" message="Please contact us to choose your dates." />;
  if (page.isLoading || general.isLoading || isMarketplace) return <BookingLoading rows={4} />;
  if (page.isError) {
    const status = page.error instanceof BookingApiError ? page.error.status : 0;
    // 307: every date has happened, or the bundle has expired (v1 sent this
    // to a /classroom page that no longer exists; the booking is the place).
    if (status === 307) {
      return (
        <BookingProblem
          title="There are no dates left to choose"
          message="All of this bundle’s classes have taken place, or the bundle has expired."
          action={
            <a href={ctx.links.manage(bookingId)} className="text-sm font-medium text-primary underline underline-offset-4">
              View your booking
            </a>
          }
        />
      );
    }
    return status === 404 ? (
      <RecordNotFound />
    ) : (
      <BookingProblem
        title="We couldn’t load this bundle"
        message={messageOf(page.error, "Please try again in a moment.")}
        onRetry={() => page.refetch()}
      />
    );
  }

  if (elsewhere) return <RecordNotFound />;
  return (
    <div className="space-y-4">
      {back}
      <BundleDatesForm
        ctx={ctx}
        data={page.data!}
        policy={general.data?.school?.settings?.reschedulePolicy}
        token={token}
      />
    </div>
  );
}

function BundleDatesForm({
  ctx,
  data,
  policy,
  token,
}: {
  ctx: ViewContext;
  data: BundlePage;
  policy: ReschedulePolicy | undefined;
  token: string | null;
}) {
  const qc = useQueryClient();
  const course = data.course;
  const timezone = safeTimezone(data.bundle.timezone || course?.timezone);
  const viewer = viewerTimezone();
  const currency = String(data.bundle.currencyUsed || "GBP").toUpperCase();

  const [bundle, setBundle] = useState<ApiBundle>(data.bundle);
  const [sessions, setSessions] = useState<SessionLike[]>(data.sessions ?? []);
  const [originalIds, setOriginalIds] = useState<string[]>(() => recordSessionIds(data.bundle));
  const [selected, setSelected] = useState<string[]>(() => recordSessionIds(data.bundle));
  const [fullyLoaded, setFullyLoaded] = useState(false);
  const [loadProblem, setLoadProblem] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const pageRef = useRef((data.sessions ?? []).length > 0 ? 1 : 0);

  const more = useMutation({
    mutationFn: async () => {
      const next = pageRef.current + 1;
      const params = new URLSearchParams({
        page: String(next),
        limit: String(DATES_PAGE_SIZE),
        isBundle: "true",
        bundleID: String(bundle.tickets?.[0]?.id ?? ""),
      });
      const dates = await bookingJson<SessionLike[]>(
        `${ctx.api}/widget/courses/${encodeURIComponent(course._id)}/dates?${params}`,
        authInit(token),
      );
      return { next, dates: Array.isArray(dates) ? dates : [] };
    },
    onMutate: () => setLoadProblem(false),
    onSuccess: ({ next, dates }) => {
      pageRef.current = next;
      setSessions((current) => appendUnique(current, dates));
      setFullyLoaded(dates.length < DATES_PAGE_SIZE);
    },
    onError: () => setLoadProblem(true),
  });

  // No dates came with the page: fetch the first lot (v1 does the same).
  const loadMore = more.mutate;
  useEffect(() => {
    if ((data.sessions ?? []).length === 0) loadMore();
  }, [data.sessions, loadMore]);

  const save = useMutation({
    mutationFn: (sessionsIds: string[]) =>
      sendJson<ApiBundle | null>(`${ctx.api}/app/user/bundle/${encodeURIComponent(bundle._id)}/book`, "POST", { sessionsIds }, authInit(token)),
    onMutate: () => setSaved(null),
    onSuccess: (updated) => {
      const next = updated && Array.isArray(updated.bundleRecords) ? updated : bundle;
      const held = recordSessionIds(next);
      setBundle(next);
      setSelected(held);
      // The saved dates are now the booked ones the notice period protects.
      setOriginalIds(held);
      const left = classesLeft(next, held);
      setSaved(savedMessage(left, Number(next.bundleTotalTickets ?? 0), next.bundleExpireDate ? formatNumericDate(next.bundleExpireDate, timezone) : "your bundle's expiry date"));
      // Other views of this booking are stale now; this page keeps its
      // own answer so the confirmation stays on screen.
      qc.invalidateQueries({ queryKey: ["booking"], predicate: (q) => q.queryKey[1] !== "bundle-dates" });
    },
  });

  const max = Number(bundle.bundleTotalTickets ?? 0);
  const left = classesLeft(bundle, selected);
  const expires = bundle.bundleExpireDate ? formatNumericDate(bundle.bundleExpireDate, timezone) : null;
  const isSubmitting = save.isPending;
  const rows = sessions.map((s) => toSessionRow(s, { timezone, locations: data.locations, tutors: data.tutors, viewer }));
  const byId = new Map(sessions.map((s) => [s._id, s]));
  const selectedDates = selected
    .map((id) => byId.get(id))
    .filter((s): s is SessionLike => !!s)
    .map((s) => formatLongDate(s.startDateTime, timezone))
    .join(", ");

  function isDisabled(id: string): boolean {
    const session = byId.get(id);
    if (!session || isSubmitting) return true;
    if (isBundleDateDisabled({ session, bundle, originalIds, policy, timezone })) return true;
    // A full selection takes nothing new until a date is let go.
    return selected.length >= max && !selected.includes(id);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">{classesLeftTitle(left)}</h1>
        <p className="text-sm text-muted-foreground">
          Select which classes you want to attend below. You can select several at once.
          {expires && (
            <>
              {" "}
              <strong className="text-foreground">You have until {expires} to select your dates.</strong>
            </>
          )}
        </p>
      </div>

      <ClassSummary title={course?.title} thumbnail={course?.thumbnail}>
        {(bundle.tickets ?? []).map((ticket: ApiBundleTicket, i: number) => (
          <span key={ticket.id ?? i} className="block">
            {(ticket.count ?? 0) > 1 ? `${ticket.count} × ` : ""}
            {ticket.name}
          </span>
        ))}
        {typeof bundle.total === "number" && <span className="block font-medium text-foreground">Total {formatCurrency(bundle.total, currency)}</span>}
      </ClassSummary>

      {saved && (
        <Notice tone="success">
          <p className="font-medium">Dates saved</p>
          <p>{saved}</p>
        </Notice>
      )}
      {save.isError && (
        <Notice tone="error">
          <p className="font-medium">Unable to save the dates</p>
          <p>
            {save.error instanceof BookingApiError && save.error.status < 500
              ? messageOf(save.error, "Please try again.")
              : "Something went wrong. Please try again."}
          </p>
        </Notice>
      )}

      {rows.length > 0 && (
        <SessionPicker
          multiple
          rows={rows}
          timezone={timezone}
          selected={selected}
          isDisabled={(row) => isDisabled(row.id)}
          onToggle={(id) =>
            setSelected((current) =>
              protectSelection({ next: toggleSelection(current, id, max, false), originalIds, sessions, policy, timezone }),
            )
          }
        />
      )}
      {rows.length === 0 && !more.isPending && fullyLoaded && (
        <Notice tone="info">There are no upcoming dates to choose from yet. Please check back later.</Notice>
      )}
      {loadProblem && <Notice tone="error">We couldn’t load more dates. Please try again.</Notice>}
      {more.isPending && <BookingLoading rows={1} />}
      {!fullyLoaded && (
        <div className="text-center">
          <Button variant="outline" disabled={more.isPending} onClick={() => more.mutate()}>
            See more dates
          </Button>
        </div>
      )}

      <div className="sticky bottom-0 flex flex-col gap-3 border-t bg-background/95 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm">
          <span className="text-muted-foreground">Dates selected: </span>
          <span className="font-medium">{selectedDates || "None yet"}</span>
        </p>
        <Button disabled={!canSaveDates(selected, bundle) || isSubmitting} onClick={() => save.mutate(selected)}>
          {isSubmitting && <Spinner className="mr-2 size-4" />}
          {saveLabel(selected.length)}
        </Button>
      </div>
    </div>
  );
}
